import React, { useRef } from "react";
import { motion, useReducedMotion, useScroll, useSpring, useTransform, type MotionValue } from "framer-motion";
import { useSite } from "../../context/SiteContext";
import { TACO_ASSEMBLED, TACO_EXPLODED, tacoIngredients, tacoPhrases, type Bilingual } from "../../data/homeContent";
import { AgaveMark } from "../culture/AgaveMark";
import { TalaveraCorner } from "../culture/TalaveraCorner";

/*
 * Scroll journey (0 → 1):
 * 0.00–0.18 assembled taco rests · 0.18–0.45 it opens into its ingredients ·
 * 0.45–0.78 ingredients float and are named · 0.78–0.95 it comes back together.
 */
export function TacoStory() {
  const {
    t
  } = useSite();
  const reduceMotion = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ["start start", "end end"]
  });
  const progress = useSpring(scrollYProgress, {
    stiffness: 80,
    damping: 22,
    mass: 0.6,
    restDelta: 0.0005
  });

  /*
   * Morph: the deconstructed taco sits underneath and grows in first, while the assembled
   * taco on top expands, softens and dissolves into it — no empty frame between them.
   * Reassembly mirrors the same motion in reverse.
   */
  const explodedOpacity = useTransform(progress, [0.06, 0.3, 0.74, 0.94], [0, 1, 1, 0]);
  const explodedScale = useTransform(progress, [0.06, 0.42, 0.6, 0.94], [0.9, 1.02, 1.06, 0.9]);
  const explodedRotate = useTransform(progress, [0.06, 0.42, 0.6, 0.94], [-6, 0, 2, -6]);
  const explodedY = useTransform(progress, [0.3, 0.5, 0.7], [6, -8, 6]);
  const explodedBlur = useTransform(progress, [0.06, 0.3, 0.74, 0.94], ["blur(4px)", "blur(0px)", "blur(0px)", "blur(4px)"]);
  const assembledOpacity = useTransform(progress, [0, 0.14, 0.42, 0.62, 0.88, 1], [1, 1, 0, 0, 1, 1]);
  const assembledScale = useTransform(progress, [0, 0.14, 0.42, 0.62, 0.88], [1, 1, 1.14, 1.14, 1]);
  const assembledRotate = useTransform(progress, [0.14, 0.42, 0.62, 0.88], [0, 4, 4, 0]);
  const assembledBlur = useTransform(progress, [0.14, 0.42, 0.62, 0.88], ["blur(0px)", "blur(5px)", "blur(5px)", "blur(0px)"]);
  const halo = useTransform(progress, [0.1, 0.5, 0.9], [0.85, 1.12, 0.85]);
  const rail = useTransform(progress, [0, 1], ["0%", "100%"]);
  if (reduceMotion) {
    return <section aria-labelledby="taco-heading" className="bg-[#FCF4E6] px-5 py-20 sm:px-8" data-id="element-311" data-mp="350fftuf0ibn4">
        <div className="mx-auto max-w-5xl text-center" data-id="element-312" data-mp="2hb4muz3wlljj">
          <Header t={t} data-id="element-313" data-mp="2hbmsungdbe4n" />
          <img src={TACO_EXPLODED} alt={t("A taco and its fresh ingredients", "Un taco y sus ingredientes frescos")} loading="lazy" className="mx-auto mt-8 w-full max-w-xl mix-blend-multiply" data-id="element-314" data-mp="2hd7ka47dygya" />
        </div>
      </section>;
  }
  return <section aria-labelledby="taco-heading" className="relative bg-[#FCF4E6]" data-id="element-315" data-mp="2m9eifatjio5o">
      <div ref={ref} className="relative h-[320svh]" data-id="element-316" data-mp="5lap17jfsoys">
        <div className="sticky top-[68px] flex h-[calc(100svh-68px)] flex-col overflow-hidden lg:flex-row lg:items-center" data-id="element-317" data-mp="5k0rqkkerkt7">
          <TalaveraCorner className="absolute left-3 top-3 w-16 opacity-60 sm:w-24" data-id="element-318" data-mp="93rffft9r4ed" />
          <TalaveraCorner className="absolute bottom-3 right-3 w-16 rotate-180 opacity-60 sm:w-24" data-id="element-319" data-mp="9645yhd3en31" />

          <div className="relative z-10 mx-auto w-full max-w-7xl px-5 pt-8 text-center sm:px-8 lg:w-[42%] lg:max-w-none lg:pl-[max(2rem,calc((100vw-80rem)/2+2rem))] lg:pt-0 lg:text-left" data-id="element-320" data-mp="e0hubztc7ty7">
            <Header t={t} data-id="element-321" data-mp="46r871konvlo" />
            <div className="relative mt-5 hidden h-14 lg:block" data-id="element-322" data-mp="ruiwl3jhzz3b">
              {tacoPhrases.map(phrase => <Phrase key={phrase.text[0]} progress={progress} range={phrase.range} text={phrase.text} data-id="element-323" data-mp="wtvjtpea786f" />)}
            </div>
            <div className="mx-auto mt-6 hidden h-[3px] w-40 overflow-hidden rounded-full bg-agavedeep/10 lg:mx-0 lg:block" data-id="element-324" data-mp="i3wixevcdin9">
              <motion.div style={{
              width: rail
            }} className="h-full bg-marigold" data-id="element-325" data-mp="n167ytetjsak" />
            </div>
          </div>

          <div className="relative flex flex-1 items-center justify-center px-3 lg:w-[58%] lg:flex-none lg:px-10" data-id="element-326" data-mp="1fjjx84m3a5y1">
            <div className="relative aspect-square w-[min(94vw,52svh)] sm:w-[min(80vw,56svh)] lg:w-[min(46vw,74svh)]" data-id="element-327" data-mp="3o165elwhfgpm">
              <motion.div aria-hidden="true" style={{
              scale: halo
            }} className="absolute inset-[12%] rounded-full bg-marigold/15" data-id="element-328" data-mp="3j108orbcv40o" />
              <motion.img src={TACO_EXPLODED} alt="" aria-hidden="true" style={{
              opacity: explodedOpacity,
              scale: explodedScale,
              rotate: explodedRotate,
              y: explodedY,
              filter: explodedBlur
            }} className="absolute inset-0 h-full w-full object-contain mix-blend-multiply will-change-transform" data-id="element-329" data-mp="394ou1uoam232" />
              <motion.img src={TACO_ASSEMBLED} alt={t("Carne asada taco with pineapple, onion, cilantro and lime", "Taco de carne asada con piña, cebolla, cilantro y limón")} style={{
              opacity: assembledOpacity,
              scale: assembledScale,
              rotate: assembledRotate,
              filter: assembledBlur
            }} className="absolute inset-0 h-full w-full object-contain mix-blend-multiply will-change-transform" data-id="element-330" data-mp="3s39j7h6mixe2" />
              {tacoIngredients.map((ingredient, index) => <IngredientLabel key={ingredient.name[0]} progress={progress} index={index} left={ingredient.left} top={ingredient.top} label={t(...ingredient.name)} data-id="element-331" data-mp="15ubo08zkbzio" data-mpi={index} />)}
            </div>
          </div>

          <div className="relative h-12 px-5 pb-8 text-center lg:hidden" data-id="element-332" data-mp="1oll26vka3cgl">
            {tacoPhrases.map(phrase => <Phrase key={phrase.text[0]} progress={progress} range={phrase.range} text={phrase.text} data-id="element-333" data-mp="1tl7ft640qupx" />)}
          </div>
        </div>
      </div>
    </section>;
}
function Header({
  t
}: {
  t: (en: string, es: string) => string;
}) {
  return <>
      <AgaveMark className="mx-auto h-8 w-12 lg:mx-0" data-id="element-334" data-mp="23ghhfsqi9036" />
      <h2 id="taco-heading" className="mt-3 font-serif text-[40px] font-bold leading-[0.95] text-agavedeep sm:text-6xl lg:text-7xl" data-id="element-335" data-mp="1dvri61ds0yse">
        {t("Every Taco Tells a Story", "Cada taco cuenta una historia")}
      </h2>
      <p className="mx-auto mt-3 max-w-md text-base leading-7 text-agavedeep/75 sm:text-lg lg:mx-0" data-id="element-336" data-mp="1nm2lblpmz3y8">
        {t("Fresh ingredients. Bold flavor. Authentic Mexican tradition.", "Ingredientes frescos. Sabor intenso. Auténtica tradición mexicana.")}
      </p>
    </>;
}
function Phrase({
  progress,
  range,
  text
}: {
  progress: MotionValue<number>;
  range: [number, number];
  text: Bilingual;
}) {
  const {
    t
  } = useSite();
  const [start, end] = range;
  const fade = 0.05;
  const opacity = useTransform(progress, [Math.max(0, start - fade), start + fade, end - fade, Math.min(1, end + fade)], [start === 0 ? 1 : 0, 1, 1, end === 1 ? 1 : 0]);
  const y = useTransform(progress, [Math.max(0, start - fade), start + fade], [start === 0 ? 0 : 12, 0]);
  return <motion.p style={{
    opacity,
    y
  }} className="absolute inset-x-0 top-0 font-serif text-[28px] font-bold italic leading-tight text-chili sm:text-4xl" data-id="element-337" data-mp="2iuen9hubefwk">
      {t(...text)}
    </motion.p>;
}
function IngredientLabel({
  progress,
  index,
  left,
  top,
  label
}: {
  progress: MotionValue<number>;
  index: number;
  left: string;
  top: string;
  label: string;
}) {
  const start = 0.4 + index * 0.025;
  const opacity = useTransform(progress, [start, start + 0.06, 0.64, 0.72], [0, 1, 1, 0]);
  const scale = useTransform(progress, [start, start + 0.06], [0.96, 1]);
  return <motion.span aria-hidden="true" style={{
    opacity,
    scale,
    left,
    top,
    x: "-50%",
    y: "-50%"
  }} className="absolute flex items-center gap-1.5 whitespace-nowrap rounded-full border border-agavedeep/10 bg-cream/95 px-2.5 py-1 text-[11px] font-bold text-agavedeep shadow-sm sm:text-xs" data-id="element-338" data-mp="1q6sc9oirc8zo">
      <span className="h-1.5 w-1.5 rounded-full bg-marigold" data-id="element-339" data-mp="3pfz0ji6x4ssl" />
      {label}
    </motion.span>;
}